import { getEdfHeaderInfo } from '@lmt/signal-viewer-processor';

export type PairedEventXml = {
  /** Multipart file name of the /events/custom request; also used in diagnostics. */
  fileName: string;
  /** The XML file itself, uploaded as-is by getCustomEvents. */
  file: File;
};

export type SignalEventPairing = {
  /** Index-aligned with the edfFiles the pairing was built from. */
  eventXmls: (PairedEventXml | undefined)[];
  unmatchedXmlNames: string[];
};

// The device stamps every event XML with its recording start in epoch seconds,
// e.g. `<!-- started 1536137333 -->`.
const STARTED_COMMENT_REGEX = /<!--\s*started\s+(\d+)\s*-->/;
const STARTED_COMMENT_SEARCH_WINDOW = 512;

const baseNameOf = (fileName: string): string => {
  const lower = fileName.toLowerCase();
  const dotIdx = lower.lastIndexOf('.');
  return dotIdx > 0 ? lower.slice(0, dotIdx) : lower;
};

// Ordered most specific first. The signal→event substitution mirrors the
// backend's ZipImporter.GetPathToEventEntries; event_<stem> mirrors pvdat
// archives.
const xmlBaseCandidatesFor = (edfBase: string): string[] => {
  const candidates = [
    edfBase.includes('signal')
      ? edfBase.replace(/signal/g, 'event')
      : undefined,
    `event_${edfBase}`,
    edfBase,
  ].filter((candidate) => candidate !== undefined);

  return [...new Set(candidates)];
};

const parseStartedEpoch = async (xmlFile: File): Promise<number | null> => {
  const head = await xmlFile.slice(0, STARTED_COMMENT_SEARCH_WINDOW).text();
  const match = STARTED_COMMENT_REGEX.exec(head);
  return match ? Number(match[1]) : null;
};

/**
 * Rank order is compared instead of absolute times because the XML `started`
 * clock and the EDF header clock differ by a constant offset. Bails out with
 * null on any unreadable epoch or header rather than pairing on partial
 * information.
 */
const tryRankPairByStartTime = async (
  edfFiles: File[],
  edfIdxs: number[],
  xmls: PairedEventXml[],
  xmlIdxs: number[],
): Promise<Map<number, number> | null> => {
  const epochs: number[] = [];
  for (const xmlIdx of xmlIdxs) {
    const epoch = await parseStartedEpoch(xmls[xmlIdx].file);
    if (epoch === null) return null;
    epochs.push(epoch);
  }

  const headerResults = await Promise.all(
    edfIdxs.map((edfIdx) => getEdfHeaderInfo(edfFiles[edfIdx])),
  );
  const startsMs: number[] = [];
  for (const result of headerResults) {
    if (!result.isSuccess) return null;
    startsMs.push(result.data.startMs);
  }

  const rankedEdfIdxs = edfIdxs
    .map((edfIdx, k) => ({ edfIdx, startMs: startsMs[k] }))
    .sort(
      (a, b) =>
        a.startMs - b.startMs ||
        edfFiles[a.edfIdx].name.localeCompare(edfFiles[b.edfIdx].name),
    )
    .map(({ edfIdx }) => edfIdx);

  const rankedXmlIdxs = xmlIdxs
    .map((xmlIdx, k) => ({ xmlIdx, epoch: epochs[k] }))
    .sort(
      (a, b) =>
        a.epoch - b.epoch ||
        xmls[a.xmlIdx].fileName.localeCompare(xmls[b.xmlIdx].fileName),
    )
    .map(({ xmlIdx }) => xmlIdx);

  return new Map(rankedEdfIdxs.map((edfIdx, k) => [edfIdx, rankedXmlIdxs[k]]));
};

/**
 * Matches event XMLs to EDFs by file name, then by recording start — browser
 * file order says nothing about which XML belongs to which EDF. Files that
 * can't be matched are reported as unmatched instead of being paired by
 * position (same approach as P2_8EventsProvider on the backend).
 */
export async function pairSignalsWithEventXmls(
  edfFiles: File[],
  xmlFiles: File[],
): Promise<SignalEventPairing> {
  const eventXmls: (PairedEventXml | undefined)[] = new Array(
    edfFiles.length,
  ).fill(undefined);

  const xmls: PairedEventXml[] = xmlFiles.map((file) => ({
    fileName: file.name,
    file,
  }));

  const unclaimedXmlIdxsByBase = new Map<string, number[]>();
  xmls.forEach((xml, xmlIdx) => {
    const base = baseNameOf(xml.fileName);
    const queue = unclaimedXmlIdxsByBase.get(base);
    if (queue) {
      queue.push(xmlIdx);
    } else {
      unclaimedXmlIdxsByBase.set(base, [xmlIdx]);
    }
  });

  // Claim in name order so contested XMLs resolve deterministically regardless
  // of the order the browser delivered the files in.
  const edfClaimOrder = edfFiles
    .map((_, edfIdx) => edfIdx)
    .sort((a, b) => edfFiles[a].name.localeCompare(edfFiles[b].name));

  const unmatchedEdfIdxs: number[] = [];
  for (const edfIdx of edfClaimOrder) {
    const base = baseNameOf(edfFiles[edfIdx].name);
    let claimedXmlIdx: number | undefined;

    for (const candidate of xmlBaseCandidatesFor(base)) {
      claimedXmlIdx = unclaimedXmlIdxsByBase.get(candidate)?.shift();
      if (claimedXmlIdx !== undefined) break;
    }

    if (claimedXmlIdx !== undefined) {
      eventXmls[edfIdx] = xmls[claimedXmlIdx];
    } else {
      unmatchedEdfIdxs.push(edfIdx);
    }
  }

  const unmatchedXmlIdxs = [...unclaimedXmlIdxsByBase.values()]
    .flat()
    .sort((a, b) => a - b);

  if (unmatchedEdfIdxs.length === 1 && unmatchedXmlIdxs.length === 1) {
    eventXmls[unmatchedEdfIdxs[0]] = xmls[unmatchedXmlIdxs[0]];
    return { eventXmls, unmatchedXmlNames: [] };
  }

  if (
    unmatchedEdfIdxs.length > 0 &&
    unmatchedEdfIdxs.length === unmatchedXmlIdxs.length
  ) {
    const rankPairs = await tryRankPairByStartTime(
      edfFiles,
      unmatchedEdfIdxs,
      xmls,
      unmatchedXmlIdxs,
    );

    if (rankPairs) {
      for (const [edfIdx, xmlIdx] of rankPairs) {
        eventXmls[edfIdx] = xmls[xmlIdx];
      }
      return { eventXmls, unmatchedXmlNames: [] };
    }
  }

  return {
    eventXmls,
    unmatchedXmlNames: unmatchedXmlIdxs.map((xmlIdx) => xmls[xmlIdx].fileName),
  };
}
